import { useId } from 'react'
import type { JSX } from 'react'
import type { Famille, Palier } from '@whatremains/shared'
import { PALIER_COULEUR } from '@whatremains/shared'
import { GlypheFamille } from './Glyphes'
import { TRACES } from './traces'
import { ICONES_PEINTES } from './peintes'

/**
 * Le générateur d'icônes d'objet.
 *
 *     cadre(palier) + silhouette(famille) + teinte(materiau)
 *
 * Les trois briques existaient séparément : les huit couleurs de palier dans
 * `enums.ts`, les dix-sept silhouettes dans `Glyphes.tsx`, les quinze teintes de
 * matériau en base. Ce fichier est le seul endroit où elles se rencontrent.
 *
 * **La loi, `01-SYSTEMES.md` « Règle de couleur » : le cadre dit le palier,
 * l'illustration dit l'objet.** Le cadre est donc peint par `PALIER_COULEUR` et
 * par rien d'autre ; la silhouette est teintée par le matériau et jamais par le
 * palier. Deux pièces du même palier se ressemblent de loin par leur cadre et ne
 * se confondent jamais de près par leur silhouette et leur teinte.
 *
 * Ce que ça vaut en catalogue : 17 familles × 15 matériaux × 8 paliers = 2040
 * icônes, quelques kilo-octets de tracé, nettes à toute taille.
 *
 * ── Comment le cadre distingue huit paliers sans compter sur la couleur ──────
 *
 * Un joueur qui distingue mal les couleurs doit voir la différence. La couleur
 * n'est donc jamais le seul porteur : la **forme** du cadre monte d'un cran à
 * chaque palier, et chaque cran est une différence qui se compte ou se voit en
 * silhouette, jamais une nuance d'épaisseur qu'il faudrait comparer côte à côte.
 *
 *   0 standard    carré aux coins OUVERTS, trait fin. Rien n'est fermé.
 *   1 commun      le carré se ferme.
 *   2 fin         les coins se coupent : la silhouette devient octogonale.
 *   3 rare        quatre ergots traversent le cadre au milieu des côtés.
 *   4 épique      un filet intérieur double le contour.
 *   5 légendaire  quatre ergots de plus, sur les coupes de coin. Huit en tout.
 *   6 mythique    un anneau extérieur ceinture le tout ; les ergots deviennent
 *                 les entretoises qui relient les deux bandes.
 *   7 ultime      les huit ergots se dédoublent (seize), et le trait est irisé.
 *
 * Rien ne disparaît d'un palier au suivant : on ajoute. C'est ce qui rend la
 * progression lisible même quand deux pièces ne sont pas côte à côte.
 *
 * L'ordre des ajouts n'est pas indifférent, et il a été corrigé après avoir
 * regardé la planche à 32 px : les deux crans qui ne font que compter des
 * ergots (0→4 puis 4→8) se ressemblaient trop quand ils se suivaient. Le filet
 * intérieur, qui est le changement le plus visible de la série, a donc été
 * glissé entre les deux.
 *
 * Le registre reste celui du jeu : du trait, et rien d'autre. Aucune lumière,
 * aucune ombre portée, aucun dégradé décoratif. La seule exception est l'irisé
 * du palier 07, qui est canon (`01-SYSTEMES.md`) et le seul élément animé du
 * jeu : `PALIER_COULEUR.ultime` vaut `null` parce que ce palier n'a pas de
 * couleur, il a un blanc qui joue vers le rose, le vert d'eau et le violet pâle
 * sans jamais devenir une couleur franche.
 */

/* ── Le repère ────────────────────────────────────────────────────────────
 *
 * Tout est écrit dans un carré de 32 unités. Les valeurs ci-dessous sont les
 * seules à toucher : le reste du fichier les compose.
 *
 * Le budget est serré et c'est voulu : à 32 px, une unité vaut un pixel, donc
 * un détail de moins de deux unités n'existe pas. Chaque bande a été mesurée
 * pour survivre à cette taille.
 */
const N = 32

/** Retrait du contour. Ce qui reste dehors (3.6) loge les ergots et l'anneau. */
const BORD = 3.6
/** Longueur de la coupe de coin, à partir du palier « fin ». */
const COUPE = 5.0
/**
 * Vide laissé aux quatre coins du carré ouvert, au palier « standard ».
 *
 * Il était à 3.6 au premier jet : à 32 px, les quatre segments se détachaient
 * au point de ne plus se lire comme un cadre du tout, mais comme quatre barres
 * posées là. Un coin ouvert doit rester un coin.
 */
const VIDE = 2.6

/** Les deux bouts d'un ergot, mesurés depuis le bord du repère. */
const ERGOT_POINTE = 1.0
const ERGOT_PIED = 4.9
/** Écart entre les deux barres d'un ergot dédoublé (palier « ultime »). */
const ERGOT_ECART = 2.0

/** Retrait du filet intérieur, mesuré depuis le contour. */
const FILET = 2.6
/**
 * Avancée de l'anneau extérieur, mesurée depuis le contour.
 *
 * Poussée au maximum que laisse le repère : à 2.4, l'anneau et le contour se
 * touchaient presque à 32 px et le mythique ne se distinguait plus du
 * légendaire autrement que par sa couleur, ce qui est exactement ce qu'on ne
 * veut pas.
 */
const ANNEAU = 2.7

/**
 * Le côté du glyphe et sa position.
 *
 * Le tracé d'un glyphe occupe `[2.7, 21.4]` de son repère de 24, soit 78 % de
 * son côté. À 21, son encre court donc de 7.9 à 24.1, ce qui laisse plus d'une
 * unité de dégagement avec le filet intérieur : le glyphe respire sans jamais
 * toucher le cadre, même au palier le plus chargé. Il occupe la moitié du côté
 * de l'icône, soit la proportion qu'avait déjà le glyphe nu dans sa case.
 */
const GLYPHE = 21.0
const GLYPHE_POS = (N - GLYPHE) / 2

/* ── Les teintes de matériau ──────────────────────────────────────────────
 *
 * Recopiées de la table `materiau` (colonne `couleur`, pas `teinte`), qui les
 * porte déjà toutes les quinze. Le client ne reçoit que le CODE du matériau
 * dans `ObjetDTO`, jamais sa couleur : il faut donc bien que la correspondance
 * vive quelque part côté navigateur. C'est une copie, donc une dette : elle est
 * écrite dans `05-EN-ATTENTE.md`.
 *
 * Elles ont été choisies sobres, et c'est ce qui tient la règle : la teinte la
 * plus saturée du lot (bronze, 48 %) reste sous la moins saturée des couleurs
 * de palier haut (fin, 51 %). Une teinte de matériau ne peut donc pas prendre
 * le dessus sur un cadre de haut palier, quel que soit le croisement. Un
 * mythique en bois reste lisible comme mythique.
 */
export const MATERIAU_TEINTE: Record<string, string> = {
  acier: '#B7BCC4',
  ardoise: '#4B535C',
  argent: '#A9AEB4',
  bois: '#7A5C3E',
  bronze: '#A8763C',
  chene: '#5B462E',
  corne: '#8B7A5F',
  cuir: '#6B4A32',
  etoupe: '#9A8E74',
  fer: '#8A8D92',
  fonte: '#55585D',
  lin: '#B8AE96',
  os: '#D8D2C0',
  plomb: '#6E7176',
  suif: '#C6BE9A',
}

/**
 * Faute de matériau, le neutre.
 *
 * `OffreDTO` ne porte pas de matériau : l'échoppe vend un modèle, pas un
 * exemplaire. La silhouette prend alors l'encre atténuée de l'interface, ce qui
 * dit exactement ce qui est vrai — la matière n'est pas dite. On n'invente pas
 * un matériau pour combler.
 */
const TEINTE_NEUTRE = 'var(--ink-dim)'

/** L'irisé du palier 07, repris tel quel de `01-SYSTEMES.md`. */
const IRISE = ['#FFF8E6', '#F1E2BC', '#EBD9F2', '#D9EEF4', '#F7EBCB', '#FFFDF6', '#EFE0BA']

/* ── Les recettes de cadre ────────────────────────────────────────────────── */

type Contour = 'ouvert' | 'carre' | 'octogone'

type Recette = {
  contour: Contour
  /** Épaisseur du contour, en unités du repère. */
  trait: number
  /** Quatre ergots au milieu des côtés. */
  ergotsMedians: boolean
  /** Quatre ergots de plus, sur les coupes de coin. */
  ergotsCoins: boolean
  /** Un filet qui double le contour par l'intérieur. */
  filet: boolean
  /** Un anneau qui ceinture le tout par l'extérieur. */
  anneau: boolean
  /** Chaque ergot devient deux barres parallèles. */
  ergotsDoubles: boolean
  /** Le trait est peint par le dégradé irisé plutôt que par une couleur. */
  irise: boolean
}

const RECETTES: Record<Palier, Recette> = {
  standard: {
    contour: 'ouvert', trait: 1.1,
    ergotsMedians: false, ergotsCoins: false, filet: false, anneau: false,
    ergotsDoubles: false, irise: false,
  },
  commun: {
    contour: 'carre', trait: 1.3,
    ergotsMedians: false, ergotsCoins: false, filet: false, anneau: false,
    ergotsDoubles: false, irise: false,
  },
  fin: {
    contour: 'octogone', trait: 1.3,
    ergotsMedians: false, ergotsCoins: false, filet: false, anneau: false,
    ergotsDoubles: false, irise: false,
  },
  rare: {
    contour: 'octogone', trait: 1.4,
    ergotsMedians: true, ergotsCoins: false, filet: false, anneau: false,
    ergotsDoubles: false, irise: false,
  },
  epique: {
    contour: 'octogone', trait: 1.6,
    ergotsMedians: true, ergotsCoins: false, filet: true, anneau: false,
    ergotsDoubles: false, irise: false,
  },
  legendaire: {
    contour: 'octogone', trait: 1.7,
    ergotsMedians: true, ergotsCoins: true, filet: true, anneau: false,
    ergotsDoubles: false, irise: false,
  },
  mythique: {
    contour: 'octogone', trait: 1.9,
    ergotsMedians: true, ergotsCoins: true, filet: true, anneau: true,
    ergotsDoubles: false, irise: false,
  },
  ultime: {
    contour: 'octogone', trait: 1.9,
    ergotsMedians: true, ergotsCoins: true, filet: true, anneau: true,
    ergotsDoubles: true, irise: true,
  },
}

/* ── La géométrie ─────────────────────────────────────────────────────────── */

/** Arrondi à deux décimales : un tracé lisible vaut mieux qu'un tracé exact. */
const d2 = (v: number) => Math.round(v * 100) / 100

/** Le carré fermé, retiré de `b`. */
function carre(b: number): string {
  return `M ${d2(b)} ${d2(b)} H ${d2(N - b)} V ${d2(N - b)} H ${d2(b)} Z`
}

/**
 * Le même carré, mais les quatre coins manquent.
 *
 * C'est le palier standard, et l'image est juste : rien n'y est fermé. C'est
 * aussi ce qui le sépare du commun sans avoir à comparer deux épaisseurs.
 */
function carreOuvert(b: number, vide: number): string {
  const a = d2(b + vide)
  const z = d2(N - b - vide)
  const f = d2(N - b)
  const p = d2(b)
  return `M ${a} ${p} H ${z} M ${f} ${a} V ${z} M ${z} ${f} H ${a} M ${p} ${z} V ${a}`
}

/** L'octogone : le carré dont les quatre coins sont coupés sur `c`. */
function octogone(b: number, c: number): string {
  const a = d2(b + c)
  const z = d2(N - b - c)
  const f = d2(N - b)
  const p = d2(b)
  return `M ${a} ${p} H ${z} L ${f} ${a} V ${z} L ${z} ${f} H ${a} L ${p} ${z} V ${a} Z`
}

/**
 * Le décalage d'un octogone parallèle à lui-même.
 *
 * Retirer le contour de `d` vers l'intérieur ne suffit pas : la coupe de coin
 * doit rétrécir d'autant, sinon les deux tracés se croisent aux angles. La
 * coupe suit `c + d(2 - √2)`, ce qui garde les deux chanfreins parallèles.
 * `d` est positif vers l'extérieur.
 */
function coupeDecalee(c: number, d: number): number {
  return c + d * (2 - Math.SQRT2)
}

/**
 * Un ergot : une barre courte qui TRAVERSE le cadre.
 *
 * Elle ne se contente pas de dépasser vers l'extérieur. À 32 px, la saillie
 * seule ne ferait qu'une unité et demie, la moitié mangée par l'épaisseur du
 * contour. En traversant, l'ergot se voit des deux côtés du trait et se lit
 * comme un rivet, ce qui tient jusqu'en bas de l'échelle.
 *
 * `angle` en radians, mesuré depuis le centre : 0 = à droite.
 */
function ergot(angle: number, surCoupe: boolean, ecart: number): string[] {
  const ux = Math.cos(angle)
  const uy = Math.sin(angle)
  // Où passe le contour dans cette direction, mesuré depuis le centre. Sur un
  // côté, c'est la demi-largeur moins le retrait ; sur une coupe de coin, c'est
  // la distance du centre au chanfrein, qui n'est pas la même chose.
  const rContour = surCoupe ? (N - (2 * BORD + COUPE)) / Math.SQRT2 : N / 2 - BORD
  const rPointe = rContour + (BORD - ERGOT_POINTE)
  const rPied = rContour - (ERGOT_PIED - BORD)
  // Perpendiculaire, pour dédoubler la barre sans la faire pivoter.
  const decalages = ecart > 0 ? [-ecart / 2, ecart / 2] : [0]
  return decalages.map((o) => {
    const ox = -uy * o
    const oy = ux * o
    const x1 = d2(N / 2 + ux * rPointe + ox)
    const y1 = d2(N / 2 + uy * rPointe + oy)
    const x2 = d2(N / 2 + ux * rPied + ox)
    const y2 = d2(N / 2 + uy * rPied + oy)
    return `M ${x1} ${y1} L ${x2} ${y2}`
  })
}

/** Les quatre milieux de côté. */
const ANGLES_MEDIANS = [0, Math.PI / 2, Math.PI, -Math.PI / 2]
/** Les quatre coupes de coin. */
const ANGLES_COINS = [Math.PI / 4, (3 * Math.PI) / 4, (-3 * Math.PI) / 4, -Math.PI / 4]

/**
 * Le tracé d'un cadre, en une liste de segments et leur épaisseur.
 *
 * Sorti de la fonction de rendu pour rester regardable : c'est de la géométrie,
 * pas du React.
 */
function tracerCadre(r: Recette): Array<{ d: string; trait: number }> {
  const morceaux: Array<{ d: string; trait: number }> = []

  if (r.anneau) {
    morceaux.push({
      d: octogone(BORD - ANNEAU, coupeDecalee(COUPE, ANNEAU)),
      trait: d2(r.trait * 0.42),
    })
  }

  const ecart = r.ergotsDoubles ? ERGOT_ECART : 0
  const traitErgot = d2(r.trait * 0.75)
  if (r.ergotsMedians) {
    for (const a of ANGLES_MEDIANS) {
      for (const d of ergot(a, false, ecart)) morceaux.push({ d, trait: traitErgot })
    }
  }
  if (r.ergotsCoins) {
    for (const a of ANGLES_COINS) {
      for (const d of ergot(a, true, ecart)) morceaux.push({ d, trait: traitErgot })
    }
  }

  if (r.contour === 'ouvert') morceaux.push({ d: carreOuvert(BORD, VIDE), trait: r.trait })
  else if (r.contour === 'carre') morceaux.push({ d: carre(BORD), trait: r.trait })
  else morceaux.push({ d: octogone(BORD, COUPE), trait: r.trait })

  if (r.filet) {
    morceaux.push({
      d: octogone(BORD + FILET, coupeDecalee(COUPE, -FILET)),
      trait: d2(r.trait * 0.5),
    })
  }

  return morceaux
}

/* ── Le composant ─────────────────────────────────────────────────────────── */

/**
 * L'icône d'un objet.
 *
 * Décorative : le nom de la pièce est toujours écrit à côté, et sa rareté avec
 * lui. Une icône qui devrait porter le sens toute seule serait de toute façon
 * une mauvaise icône, et le lecteur d'écran n'a rien à faire d'un cadre.
 */
/**
 * Les icônes peintes, quand il y en a une.
 *
 * ## Pourquoi elles priment sur le tracé
 *
 * Enzo, en jouant : « les items se ressemblent tous, on fait pas du tout la
 * différence, c'est chiant ». Mesuré, il avait raison, et le défaut n'était pas
 * dans le système mais dans ce que le monde en sollicite : entre le niveau 1 et
 * 10 il ne croise que **deux paliers sur huit** et trois matériaux sur quinze.
 * Deux des trois porteurs de sens ne travaillaient donc pas encore, et il ne
 * restait qu'un petit trait de 38 px pour tout distinguer.
 *
 * Une image peinte porte la rareté, la matière et l'objet d'un seul coup d'œil,
 * ce que trois briques additionnées ne font qu'à condition de varier.
 *
 * ## La convention de nommage, et elle est faite pour être remplie petit à petit
 *
 * On cherche dans cet ordre, et on s'arrête au premier trouvé :
 *
 *   1. `/icones/<code du modèle>.png` — l'icône d'une pièce précise ;
 *   2. `/icones/<famille>-<palier>.png` — l'icône d'une famille à un palier ;
 *   3. le tracé vectoriel de ce fichier, qui n'est pas jeté et reste le filet.
 *
 * Le second niveau est celui qui compte : c'est exactement la façon dont les
 * planches sont générées, une par famille, une tuile par rareté. Déposer huit
 * fichiers fait apparaître une famille entière sans toucher à une ligne de code.
 *
 * ## Ce que ça n'enlève pas
 *
 * Rien. Là où aucune image n'existe, le cadre par palier et la teinte par
 * matériau continuent exactement comme avant, et une pièce peinte à côté d'une
 * pièce tracée reste lisible : les deux disent la même rareté, l'une par son
 * cadre dessiné, l'autre par son cadre tracé.
 */

function imagePeinte(objet: {
  famille: Famille
  palier: Palier
  code?: string
  categorie?: string
}): string | null {
  // Par code : toujours valable, puisqu'un code désigne une pièce et une seule.
  if (objet.code && ICONES_PEINTES.has(objet.code)) return `/icones/${objet.code}.png`

  /*
   * Par famille : SEULEMENT pour de l'équipement.
   *
   * Les matériaux et les consommables portent une famille de remplissage — la
   * colonne est obligatoire, il a bien fallu écrire quelque chose — et
   * « Ferraille » se trouve rangée en `masse`. Sans cette garde, elle prenait
   * l'icône peinte d'une masse dès que la famille en a reçu une. Trouvé par
   * Enzo en regardant ses sacs, et ça se serait reproduit à chaque nouvelle
   * planche.
   *
   * Faute de catégorie, on s'abstient : mieux vaut une silhouette tracée
   * qu'une image fausse.
   */
  if (objet.categorie !== 'equipement') return null
  const parFamille = `${objet.famille}-${objet.palier}`
  return ICONES_PEINTES.has(parFamille) ? `/icones/${parFamille}.png` : null
}

/**
 * Ce qu'il faut d'une pièce pour la dessiner, et pourquoi rien n'est facultatif.
 *
 * `code` et `categorie` étaient optionnels. Les oublier compilait, et la pièce
 * retombait en silence sur la silhouette tracée : pas d'erreur, pas d'écran
 * vide, juste une image moins bonne que celle qui existait. Le défaut est
 * apparu **trois fois** — les quatre listes du marché, l'atelier, la confrérie —
 * et chaque fois il a fallu qu'Enzo le voie en jouant, parce que rien dans le
 * code ne pouvait le signaler.
 *
 * Les deux champs sont donc exigés. Une pièce qui n'a pas de code n'existe pas,
 * et la catégorie décide de tout : les matériaux portent une `famille` de
 * remplissage — « Ferraille » est rangée en `masse` — donc sans elle on choisit
 * entre afficher l'icône d'une masse et n'afficher rien.
 */
export interface PieceDessinable {
  code: string
  categorie: 'equipement' | 'materiau' | 'consommable'
  famille: Famille
  palier: Palier
  /** Teinte la silhouette. Vide pour un modèle vendu sans exemplaire. */
  materiau: string
}

export function IconeObjet({
  objet,
  taille,
}: {
  objet: PieceDessinable
  taille?: number
}): JSX.Element {
  /*
   * Le tracé dessiné, quand la pièce en a un.
   *
   * Cent quarante-cinq tracés de game-icons.net remplacent les dix-sept
   * silhouettes faites à la main : chaque pièce a le sien, au lieu d'en
   * partager un avec les huit autres de sa famille. C'est le porteur de sens
   * qui manquait le plus, puisque le palier et le matériau varient à peine à
   * bas niveau.
   *
   * Ils sont posés en MASQUE et non en image, ce qui garde la teinte du
   * matériau : le fichier dit la forme, la couleur reste au monde. Le cadre du
   * palier, dessous, ne bouge pas d'un trait.
   */
  const trace = objet.code && TRACES.has(objet.code) ? `/icones/traits/${objet.code}.svg` : null

  const peinte = imagePeinte(objet)
  if (peinte) {
    return (
      <img
        className="icone-objet icone-peinte"
        src={peinte}
        width={taille ?? 48}
        height={taille ?? 48}
        alt=""
        aria-hidden="true"
        // Une icône qui n'arriverait pas laisserait un cadre vide. On préfère
        // qu'elle disparaisse : le nom de la pièce est toujours écrit à côté.
        onError={(e) => { e.currentTarget.style.visibility = 'hidden' }}
      />
    )
  }

  // Un dégradé se référence par identifiant : deux icônes ultimes sur le même
  // écran ne doivent pas se voler leur définition.
  const cle = useId()
  const cote = taille ?? 48
  const recette = RECETTES[objet.palier]
  const morceaux = tracerCadre(recette)

  // Le cadre dit le palier, et rien que le palier.
  const peinture = recette.irise ? `url(#${cle}-irise)` : (PALIER_COULEUR[objet.palier] ?? 'currentColor')
  // La silhouette dit l'objet, et rien que l'objet.
  const teinte = MATERIAU_TEINTE[objet.materiau] ?? TEINTE_NEUTRE

  const cadre = (
    <svg
      className="icone-objet"
      viewBox={`0 0 ${N} ${N}`}
      width={cote}
      height={cote}
      aria-hidden="true"
      focusable="false"
    >
      {recette.irise && (
        <defs>
          {/*
            L'irisé du palier 07. `reflect` fait se répondre les deux bouts du
            dégradé, ce qui permet de le faire glisser indéfiniment sans jamais
            montrer de raccord. Onze secondes, c'est la durée du canon.
          */}
          <linearGradient
            id={`${cle}-irise`}
            gradientUnits="userSpaceOnUse"
            x1="-4"
            y1="0"
            x2="36"
            y2="7"
            spreadMethod="reflect"
          >
            {IRISE.map((c, i) => (
              <stop key={c + i} offset={`${(i / (IRISE.length - 1)) * 100}%`} stopColor={c} />
            ))}
            <animateTransform
              attributeName="gradientTransform"
              type="translate"
              values="0 0; 80 14"
              dur="11s"
              repeatCount="indefinite"
            />
          </linearGradient>
        </defs>
      )}

      <g
        className="icone-cadre"
        fill="none"
        stroke={peinture}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {morceaux.map((m, i) => (
          <path key={i} d={m.d} strokeWidth={m.trait} />
        ))}
      </g>

      {/*
        La silhouette est réutilisée telle quelle. `currentColor` la traverse :
        il suffit de poser la teinte du matériau sur le groupe, et le glyphe la
        prend. Son épaisseur de trait est reprise en CSS, une largeur écrite
        pour un repère de 24 étant trop lourde une fois ramenée ici.
      */}
      {!trace && (
        <g
          className="icone-silhouette"
          style={{ color: teinte }}
          transform={`translate(${GLYPHE_POS} ${GLYPHE_POS})`}
        >
          <GlypheFamille famille={objet.famille} taille={GLYPHE} />
        </g>
      )}
    </svg>
  )

  // Sans tracé, l'icône est ce qu'elle a toujours été : un seul SVG.
  if (!trace) return cadre

  // Avec, on superpose le masque au cadre, exactement dans la place que la
  // silhouette occupait — même centre, même côté — pour que deux pièces posées
  // l'une à côté de l'autre, l'une tracée et l'autre pas, ne se décalent pas.
  return (
    <span
      className="icone-objet icone-avec-trace"
      style={{ width: cote, height: cote }}
      aria-hidden="true"
    >
      {cadre}
      <span
        className="icone-trace"
        style={{
          backgroundColor: teinte,
          maskImage: `url(${trace})`,
          WebkitMaskImage: `url(${trace})`,
          inset: `${(GLYPHE_POS / N) * 100}%`,
        }}
      />
    </span>
  )
}
